import { supabase } from './supabase';

/**
 * VOY-406: typed error thrown by checkout requests so callers can distinguish
 * CART_EXPIRED (HTTP 410) from generic failures without string matching.
 */
export class CheckoutRequestError extends Error {
  readonly status: number
  readonly code?: string
  readonly expiredAt?: string
  // VOY-551: on 409 CHANGED_PRICE, the total the page showed and the total the server would charge.
  readonly previous?: number | null
  readonly current?: number
  constructor(
    status: number,
    message: string,
    code?: string,
    expiredAt?: string,
    totals?: { previous?: number | null; current?: number },
  ) {
    super(message)
    this.name = 'CheckoutRequestError'
    this.status = status
    this.code = code
    this.expiredAt = expiredAt
    this.previous = totals?.previous
    this.current = totals?.current
  }
}

/**
 * VOY-551: when checkout refused because the total changed since the user reviewed it, the old and
 * new totals to show before asking. Null for any other error (including a CHANGED_PRICE without
 * a new total, e.g. a hotel room that is gone, which needs a new search, not a confirmation).
 */
export function priceChangeOf(err: unknown): { previous: number | null; current: number } | null {
  if (!(err instanceof CheckoutRequestError)) return null
  if (err.status !== 409 || err.code !== 'CHANGED_PRICE') return null
  if (typeof err.current !== 'number' || !Number.isFinite(err.current)) return null
  return { previous: typeof err.previous === 'number' ? err.previous : null, current: err.current }
}

/**
 * VOY-554: checkout refused because the user's paid subscription has a price that maps to no tier
 * (409 TIER_UNRESOLVED). Nothing was charged; it is not a price change and retrying will not help.
 */
export const TIER_UNRESOLVED_MESSAGE =
  "We couldn't confirm your membership, so nothing was charged. Please contact support and we'll sort it out."

export function isTierUnresolved(err: unknown): boolean {
  return err instanceof CheckoutRequestError && err.code === 'TIER_UNRESOLVED'
}

export interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  type: 'flight' | 'hotel';
  metadata?: Record<string, unknown>;
}

export interface CustomerInfo {
  name: string;
  email: string;
  phone?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
}

export interface CheckoutSessionRequest {
  items: CartItem[];
  customerInfo: CustomerInfo;
  totalAmount: number;
  currency?: string;
  singleLine?: boolean;
  successUrl: string;
  cancelUrl: string;
  cartId?: string;
  userId?: string;
  // VOY-551: the new total the user confirmed after a 409 CHANGED_PRICE.
  acceptedTotal?: number;
}

export async function createCheckoutSession(request: CheckoutSessionRequest): Promise<{ url: string; sessionId: string }> {
  try {
    // Get current session for authorization
    const { data: { session } } = await supabase.auth.getSession();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add authorization header if user is authenticated
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`;
    } else {
      // Use anon key for unauthenticated users
      headers['Authorization'] = `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`;
    }

    // Use VITE_SUPABASE_URL + /functions/v1 for consistency
    const baseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const functionsUrl = baseUrl ? `${baseUrl}/functions/v1` : '';
    const response = await fetch(`${functionsUrl}/cart-checkout`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: { error?: string; code?: string; expiredAt?: string; previous?: number | null; current?: number };
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      throw new CheckoutRequestError(
        response.status,
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        errorData.code,
        errorData.expiredAt,
        { previous: errorData.previous, current: errorData.current },
      );
    }

    const data = await response.json();

    if (!data.url) {
      throw new CheckoutRequestError(500, 'No checkout URL returned from server');
    }

    return {
      url: data.url,
      sessionId: data.sessionId
    };
  } catch (error) {
    console.error('Checkout session creation failed:', error);
    throw error;
  }
}

export interface CheckoutSessionResponse {
  url?: string
  sessionId?: string
  success_url?: string
  amount_total?: number
  customer_email?: string
  customer_details?: {
    email?: string
  }
  payment_status?: string
  [key: string]: unknown
}

export async function getCheckoutSession(sessionId: string): Promise<CheckoutSessionResponse> {
  try {
    // Get current session for authorization
    const { data: { session } } = await supabase.auth.getSession();

    // VOY-566: cart-session now requires a real user JWT and enforces that the
    // session belongs to the caller. There is no anon-key fallback — callers of
    // this function run behind ProtectedRoute, so a session must exist.
    if (!session?.access_token) {
      throw new CheckoutRequestError(401, 'You must be signed in to view this checkout session.');
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    };

    // Use VITE_SUPABASE_URL + /functions/v1 for consistency
    const baseUrl = import.meta.env.VITE_SUPABASE_URL || '';
    const functionsUrl = baseUrl ? `${baseUrl}/functions/v1` : '';
    const response = await fetch(
      `${functionsUrl}/cart-session?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      let errorData: { error?: string; code?: string; expiredAt?: string };
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: `HTTP ${response.status}: ${response.statusText}` };
      }
      throw new CheckoutRequestError(
        response.status,
        errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        errorData.code,
        errorData.expiredAt,
      );
    }

    return response.json();
  } catch (error) {
    console.error('Session retrieval failed:', error);
    throw error;
  }
}
